import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
} from "@mui/material";
import { CloseOutlined } from "@ant-design/icons";
import "../css/EditAccountDialog.css";
import { ACCOUNT_ROLE_STATUS } from "../../utils/constants";
import { StorageContext } from "../../services/StorageProvider";
import { getAirportCode, getAirportData } from "../../utils/utils";

const EditAccountDialog = ({
  open,
  selectedProfile,
  onClickSave,
  onClose,
  isLoading,
  airlineData,
}) => {
  const { airportData } = useContext(StorageContext);
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [selectedAirlineAccess, setSelectedAirlineAccess] = useState(null);
  const [selectedAirports, setSelectedAirports] = useState([]);
  const [showSelectContainer, setShowSelectContainer] = useState(false);

  useEffect(() => {
    setSelectedAccountType(selectedProfile?.role);
    setSelectedAirlineAccess(selectedProfile?.airlineId);
    const airportIds =
      selectedProfile?.airports?.map((airport) => airport?.id) || [];
    setSelectedAirports(airportIds);
  }, [open]);

  const handleAccountTypeChange = (event) => {
    setSelectedAccountType(event.target.value);
  };

  const handleAirlineAccessChange = (event) => {
    setSelectedAirlineAccess(event.target.value);
  };

  const handleAirportToggle = (airportId) => {
    setSelectedAirports((prevSelected) => {
      if (prevSelected.includes(airportId)) {
        if (prevSelected.length === 1) {
          return prevSelected;
        }
        return prevSelected.filter((id) => id !== airportId);
      } else {
        return [...prevSelected, airportId];
      }
    });
  };

  const handleSaveClick = () => {
    onClickSave(
      selectedProfile?.profileId,
      selectedAirports,
      selectedAccountType,
      selectedAirlineAccess
    );
  };

  const isValidToSave = () => {
    return !selectedAccountType || selectedAirports.length <= 0;
  };

  return (
    <Dialog
      open={open ?? false}
      onClose={onClose}
      PaperProps={{
        className: "edit-account-dialog-container",
      }}
    >
      <DialogContent>
        <CloseOutlined
          className="edit-account-dialog-close"
          onClick={onClose}
        />
        <div className="edit-account-dialog-content">
          <div className="edit-account-dialog-title">Edit</div>
          <div className="edit-account-input-container">
            <label>First Name</label>
            <div className="edit-account-non-editable-field">
              {selectedProfile?.firstName}
            </div>
          </div>
          <div className="edit-account-input-container">
            <label>Last Name</label>
            <div className="edit-account-non-editable-field">
              {selectedProfile?.lastName}
            </div>
          </div>
          <div className="edit-account-input-container">
            <label>Email</label>
            <div className="edit-account-non-editable-field">
              {selectedProfile?.email}
            </div>
          </div>
          <div className="edit-account-input-container">
            <label>Airline Account Type</label>
            <Select
              className="edit-account-select-container"
              value={selectedAccountType ?? selectedProfile?.role}
              onChange={handleAccountTypeChange}
              renderValue={(selected) =>
                ACCOUNT_ROLE_STATUS.find((type) => type.value === selected)
                  ?.name
              }
            >
              {ACCOUNT_ROLE_STATUS.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <ListItemText primary={type.name} />
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className="edit-account-input-container">
            <label>Airline Access</label>
            <Select
              className="edit-account-select-container"
              value={selectedAirlineAccess ?? selectedProfile?.airlineId}
              onChange={handleAirlineAccessChange}
              renderValue={(selected) =>
                airlineData?.data?.find((airline) => airline?.id === selected)
                  ?.airlineName
              }
            >
              {airlineData?.data?.map((airline) => (
                <MenuItem key={airline?.id} value={airline?.id}>
                  <ListItemText primary={airline?.airlineName} />
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className="edit-account-input-container">
            <label>Airport Access</label>
            <Select
              id="invite-admin-fbo-airport"
              className="invite-user-dialog-select-container"
              multiple
              value={selectedAirports}
              renderValue={(selected) =>
                selected.length > 0
                  ? selected
                      .map((id) => {
                        const airport = getAirportData(airportData, id);
                        return getAirportCode(airport);
                      })
                      .join(", ")
                  : "Select Airports"
              }
              open={showSelectContainer}
              onOpen={() => setShowSelectContainer(true)}
              onClose={() => setShowSelectContainer(false)}
            >
              <div className="invite-admin-select-list-containe">
                {airportData?.map((airport) => (
                  <MenuItem
                    key={airport?.id}
                    onClick={() => handleAirportToggle(airport?.id)}
                  >
                    <Checkbox
                      checked={selectedAirports.includes(airport?.id)}
                    />
                    <ListItemText
                      primary={`${airport?.name} - ${getAirportCode(airport)}`}
                    />
                  </MenuItem>
                ))}
              </div>
              <div className="invite-admin-select-button-container">
                <Button
                  variant="contained"
                  color="send"
                  onClick={() => setShowSelectContainer(false)}
                >
                  Add
                </Button>
              </div>
            </Select>
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={!isLoading ? handleSaveClick : null}
            endIcon={
              isLoading ? <CircularProgress size={30} color="inherit" /> : null
            }
            disabled={isValidToSave()}
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditAccountDialog;
